/**
 * Planetary Positions Test Page
 * Shows local JavaScript calculations for planetary positions
 */

import React, { useState } from 'react';
import {
  calculatePlanetPositions,
  calculateJanmaNakshatraRashi,
  calculateLagna,
} from '../utils/kundaliCalculations';
import { SIGNS } from '../data/kundaliConstants';

// Planet symbols
const PLANET_SYMBOLS = {
  Sun: '☉', Moon: '☽', Mercury: '☿', Venus: '♀', Mars: '♂',
  Jupiter: '♃', Saturn: '♄', Rahu: '☊', Ketu: '☋',
  Uranus: '♅', Neptune: '♆', Pluto: '♇'
};

export default function AccuracyTest() {
  const [form, setForm] = useState({
    date: '1990-01-01',
    time: '12:00',
    latitude: '28.6139',
    longitude: '77.2090',
    timezone: '5.5',
  });

  const [localData, setLocalData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const runCalculation = () => {
    setLoading(true);
    setError('');
    setLocalData(null);

    try {
      const [year, month, day] = form.date.split('-').map(Number);
      const [hour, minute] = form.time.split(':').map(Number);
      const lat = parseFloat(form.latitude);
      const lng = parseFloat(form.longitude);
      const tz = parseFloat(form.timezone);

      const localPlanets = calculatePlanetPositions(form.date, form.time, tz);
      const moon = localPlanets.find(p => p.name === 'Moon');
      const janma = moon ? calculateJanmaNakshatraRashi(moon.longitude) : null;
      const lagna = calculateLagna(year, month, day, hour, minute, lat, lng, tz);

      setLocalData({
        planets: localPlanets,
        janma,
        lagna,
        source: 'Local JavaScript',
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate difference between two values
  const getDiff = (backend, local) => {
    if (backend === undefined || local === undefined) return null;
    const diff = Math.abs(backend - local);
    return diff;
  };

  // Format degree
  const formatDegree = (deg) => {
    if (deg === undefined || deg === null) return '-';
    const d = Math.floor(deg);
    const m = Math.floor((deg - d) * 60);
    const s = Math.floor(((deg - d) * 60 - m) * 60);
    return `${d}°${m}'${s}"`;
  };

  // Get difference class
  const getDiffClass = (diff) => {
    if (diff === null) return '';
    if (diff < 0.1) return 'diff-excellent';
    if (diff < 0.5) return 'diff-good';
    if (diff < 1) return 'diff-fair';
    return 'diff-poor';
  };

  // Find matching planet in backend data
  const findBackendPlanet = (localPlanet) => {
    if (!backendData?.planets) return null;
    return backendData.planets.find(p =>
      p.name.toLowerCase() === localPlanet.name.toLowerCase()
    );
  };

  return (
    <div className="page" style={{ padding: '2rem' }}>
      <div className="container">
        <h1 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
          🔬 Planetary Positions Test
        </h1>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '2rem' }}>
          Local JavaScript calculations for Vedic astrology
        </p>

        {/* Input Form */}
        <div style={{
          background: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          maxWidth: '600px',
          margin: '0 auto 2rem',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                Date
              </label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                Time
              </label>
              <input
                type="time"
                name="time"
                value={form.time}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                Latitude
              </label>
              <input
                type="text"
                name="latitude"
                value={form.latitude}
                onChange={handleChange}
                placeholder="28.6139"
                style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                Longitude
              </label>
              <input
                type="text"
                name="longitude"
                value={form.longitude}
                onChange={handleChange}
                placeholder="77.2090"
                style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                Timezone (hours from UTC)
              </label>
              <input
                type="text"
                name="timezone"
                value={form.timezone}
                onChange={handleChange}
                placeholder="5.5"
                style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>
          </div>

          <button
            onClick={runComparison}
            disabled={loading}
            style={{
              marginTop: '1rem',
              width: '100%',
              padding: '0.75rem',
              background: loading ? '#ccc' : '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '1rem',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? '⏳ Calculating...' : '🔍 Run Comparison'}
          </button>
        </div>

        {/* Quick Presets */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <span style={{ color: '#666', marginRight: '0.5rem' }}>Quick Presets:</span>
          <button
            onClick={() => setForm({ date: '1990-01-01', time: '12:00', latitude: '28.6139', longitude: '77.2090', timezone: '5.5' })}
            style={{ margin: '0 0.25rem', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
          >
            Sample (Delhi)
          </button>
          <button
            onClick={() => setForm({ date: '2000-06-15', time: '06:30', latitude: '13.0827', longitude: '80.2707', timezone: '5.5' })}
            style={{ margin: '0 0.25rem', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
          >
            Chennai
          </button>
          <button
            onClick={() => setForm({ date: '1985-03-20', time: '14:45', latitude: '19.0760', longitude: '72.8777', timezone: '5.5' })}
            style={{ margin: '0 0.25rem', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
          >
            Mumbai
          </button>
          <button
            onClick={() => {
              const now = new Date();
              setForm({
                date: now.toISOString().split('T')[0],
                time: now.toTimeString().slice(0, 5),
                latitude: '28.6139',
                longitude: '77.2090',
                timezone: '5.5'
              });
            }}
            style={{ margin: '0 0.25rem', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #1976d2', background: '#e3f2fd', cursor: 'pointer' }}
          >
            Now
          </button>
        </div>

        {error && (
          <div style={{ background: '#ffebee', color: '#c62828', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {/* Results Table */}
        {(backendData || localData) && (
          <div style={{ overflowX: 'auto' }}>
            <h2 style={{ textAlign: 'center', marginBottom: '1rem' }}>📊 Comparison Results</h2>

            {/* Legend */}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <span><span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#4caf50', borderRadius: '2px', marginRight: '4px' }}></span> Excellent (&lt;0.1°)</span>
              <span><span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#8bc34a', borderRadius: '2px', marginRight: '4px' }}></span> Good (&lt;0.5°)</span>
              <span><span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#ffc107', borderRadius: '2px', marginRight: '4px' }}></span> Fair (&lt;1°)</span>
              <span><span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#ff5722', borderRadius: '2px', marginRight: '4px' }}></span> Poor (&gt;1°)</span>
            </div>

            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              background: '#fff',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}>
              <thead>
                <tr style={{ background: '#1976d2', color: 'white' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Planet</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', background: '#1565c0' }} colSpan="3">
                    🔭 Backend (Swiss Ephemeris)
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', background: '#0d47a1' }} colSpan="3">
                    📐 Local JavaScript
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', background: '#263238' }}>Diff</th>
                </tr>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ padding: '0.5rem' }}></th>
                  <th style={{ padding: '0.5rem', fontSize: '0.85rem' }}>Sign</th>
                  <th style={{ padding: '0.5rem', fontSize: '0.85rem' }}>Degree</th>
                  <th style={{ padding: '0.5rem', fontSize: '0.85rem' }}>Nakshatra</th>
                  <th style={{ padding: '0.5rem', fontSize: '0.85rem' }}>Sign</th>
                  <th style={{ padding: '0.5rem', fontSize: '0.85rem' }}>Degree</th>
                  <th style={{ padding: '0.5rem', fontSize: '0.85rem' }}>Nakshatra</th>
                  <th style={{ padding: '0.5rem', fontSize: '0.85rem' }}>°</th>
                </tr>
              </thead>
              <tbody>
                {localData?.planets?.map((localPlanet, idx) => {
                  const backendPlanet = findBackendPlanet(localPlanet);
                  const diff = backendPlanet ? getDiff(backendPlanet.longitude, localPlanet.longitude) : null;
                  const diffClass = getDiffClass(diff);

                  const diffColor = diff === null ? '#999' :
                    diff < 0.1 ? '#4caf50' :
                    diff < 0.5 ? '#8bc34a' :
                    diff < 1 ? '#ffc107' : '#ff5722';

                  return (
                    <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '0.75rem', fontWeight: 'bold' }}>
                        {PLANET_SYMBOLS[localPlanet.name] || '⭐'} {localPlanet.name}
                      </td>
                      {/* Backend */}
                      <td style={{ padding: '0.5rem', textAlign: 'center', background: '#e3f2fd' }}>
                        {backendPlanet?.sign || '-'}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', background: '#e3f2fd' }}>
                        {backendPlanet ? formatDegree(backendPlanet.degreeInSign) : '-'}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', background: '#e3f2fd', fontSize: '0.85rem' }}>
                        {backendPlanet?.nakshatra || '-'}
                      </td>
                      {/* Local */}
                      <td style={{ padding: '0.5rem', textAlign: 'center', background: '#fff3e0' }}>
                        {localPlanet.sign}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', background: '#fff3e0' }}>
                        {formatDegree(localPlanet.degree)}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', background: '#fff3e0', fontSize: '0.85rem' }}>
                        {localPlanet.nakshatra || '-'}
                      </td>
                      {/* Diff */}
                      <td style={{
                        padding: '0.5rem',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: diffColor,
                        background: diff !== null ? `${diffColor}22` : '#f5f5f5',
                      }}>
                        {diff !== null ? diff.toFixed(2) + '°' : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Summary */}
            {backendData?.planets && localData?.planets && (
              <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: '#f5f5f5',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <h3 style={{ marginBottom: '0.5rem' }}>📈 Summary</h3>
                {(() => {
                  const diffs = localData.planets
                    .map(lp => {
                      const bp = findBackendPlanet(lp);
                      return bp ? Math.abs(bp.longitude - lp.longitude) : null;
                    })
                    .filter(d => d !== null);

                  const avgDiff = diffs.length > 0 ? diffs.reduce((a, b) => a + b, 0) / diffs.length : 0;
                  const maxDiff = diffs.length > 0 ? Math.max(...diffs) : 0;
                  const excellent = diffs.filter(d => d < 0.1).length;
                  const good = diffs.filter(d => d >= 0.1 && d < 0.5).length;

                  return (
                    <div style={{ display: 'flex', gap: '2rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: avgDiff < 0.5 ? '#4caf50' : '#ff9800' }}>
                          {avgDiff.toFixed(3)}°
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#666' }}>Avg Difference</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: maxDiff < 1 ? '#4caf50' : '#ff9800' }}>
                          {maxDiff.toFixed(3)}°
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#666' }}>Max Difference</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#4caf50' }}>
                          {excellent}/{diffs.length}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#666' }}>Excellent Match</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#8bc34a' }}>
                          {excellent + good}/{diffs.length}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#666' }}>Good or Better</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Ayanamsa Info */}
            {backendData?.ayanamsa && (
              <div style={{ marginTop: '1rem', textAlign: 'center', color: '#666' }}>
                <strong>Ayanamsa (Lahiri):</strong> {backendData.ayanamsa.toFixed(6)}°
              </div>
            )}
          </div>
        )}

        {/* Info */}
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          background: '#e3f2fd',
          borderRadius: '8px',
          fontSize: '0.9rem',
          color: '#1565c0'
        }}>
          <strong>ℹ️ About This Test:</strong>
          <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
            <li><strong>Backend</strong>: Uses Swiss Ephemeris via astronomia library (high precision)</li>
            <li><strong>Local</strong>: JavaScript calculations using VSOP87 approximations</li>
            <li><strong>Expected Accuracy</strong>: Moon ±0.5°, Other planets ±0.1-0.3°</li>
            <li>Differences &lt;0.5° are acceptable for most astrological purposes</li>
          </ul>
        </div>
      </div>

      <style>{`
        .diff-excellent { background: #e8f5e9 !important; }
        .diff-good { background: #f1f8e9 !important; }
        .diff-fair { background: #fff8e1 !important; }
        .diff-poor { background: #ffebee !important; }
      `}</style>
    </div>
  );
}
